import React, { useState, useContext, useEffect, useRef } from 'react';

import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import { useNode, Element, useEditor } from '@craftjs/core';

import SettingsWrapper from '@common/components/SettingsWrapper/SettingsWrapper';
import SettingsRow from '@common/components/SettingsWrapper/SettingsRow';
import MyButton from '@common/components/Button/MyButton';
import {
  EDITOR_RESOLVERS,
  INIT_TABLE_MODAL_STATE,
  TABLE_SEGMENT_OPTION_LIST,
} from '@common/constants';

import TableSegments from '@resolvers/tools/TableSegments/TableSegments';

import { Typography } from '@mui/material';

import TableModal from './components/TableModal';
import TableRow from './components/TableRow';
import TableHeader from './components/TableHeader';
import TableFooter from './components/TableFooter';
import TableCell, { TableCellDefaultProps } from './components/TableCell';
import {
  findTableElementByParentId,
  getNodeSrc,
  findPathRecursive,
  filterNodesByParentId,
  getListOfNodes,
} from '@common/helpers/helpers';
import { useTemplateDetailsStore } from '@store/templateDetailsStore';

const Table = ({
  height,
  color,
  background,
  listSrc,
  showHeader,
  showFooter,
  children,
  elementProps = { isFixed: false },
}) => {
  const { isFixed } = elementProps;

  const INIT_TABLE_NODE_CONFIG = [
    {
      element: TableHeader,
      id: 'comp-h',
      isFixed: isFixed,
    },
    {
      element: TableRow,
      id: 'comp-r',
      isFixed: isFixed,
    },
    {
      element: TableFooter,
      id: 'comp-f',
      isFixed: isFixed,
    },
  ];

  const {
    connectors: { connect, drag },
    actions: { setCustom, setProp },
    id: tableId,
  } = useNode();

  const { query, actions } = useEditor();

  const { t } = useTranslation();
  const ref = useRef(null);

  useEffect(() => {
    if (ref?.current && connect) {
      connect(drag(ref.current));
    }
  }, [connect]);

  useEffect(() => {
    setTimeout(() => {
      actions.history.ignore(() => {
        setCustom((props) => {
          props.displayName = t('table');
        });
      });
    }, 0);
    if (isFixed) {
      setProp((props) => {
        props.listSrc = 'fixed';
        props.isFixed = isFixed;
      }, 500);
    }
  }, [setCustom, t]);

  useEffect(() => {
    const addTableRowsOnDrop = () => {
      const hasChildNodes = getListOfNodes(query).some(
        filterNodesByParentId(tableId)
      );

      if (!hasChildNodes) {
        const batched = actions.history.merge();
        INIT_TABLE_NODE_CONFIG.forEach((rowItem, rowIndex) => {
          const rowTree = query
            .parseReactElement(
              <Element
                id={rowItem.id}
                is={rowItem.element}
                canvas
                elementProps={{ isFixed: rowItem.isFixed }}
              />
            )
            .toNodeTree();

          batched.addNodeTree(rowTree, tableId, rowIndex);
        });
      }
    };

    addTableRowsOnDrop();
  }, [query, actions, tableId, INIT_TABLE_NODE_CONFIG]);

  return (
    <table
      ref={ref}
      style={{
        borderRadius: ' 4px',
        width: '100%',
        backgroundColor: background,
        color,
        tableLayout: 'fixed',
        borderCollapse: 'collapse',
        pageBreakInside: 'avoid',
        breakInside: 'avoid',
      }}
    >
      {children}
    </table>
  );
};

const TableConfigPanel = () => {
  const { source } = useTemplateDetailsStore();

  const { query, actions } = useEditor();
  const {
    actions: { setProp },
    listSrc,
    showBorder,
    parent,
    id: tableId,
    isFixed,
  } = useNode(({ data: { props, parent } }) => ({
    listSrc: props.listSrc,
    isFixed: props.isFixed,
    parent: parent,
    showBorder: props.showBorder,
  }));

  const [showModal, setShowModal] = useState(INIT_TABLE_MODAL_STATE);

  const { t } = useTranslation();

  const changeSrcValue = (newValue) => {
    setProp((props) => {
      props.listSrc = newValue;
    }, 500);
  };

  const getElementPropsByRowType = (rowNode) => {
    if (rowNode?.data?.name === EDITOR_RESOLVERS.COMMON.TABLE_HEADER)
      return { isHeader: true, isFixed: isFixed };

    if (rowNode?.data?.name === EDITOR_RESOLVERS.COMMON.TABLE_FOOTER)
      return { isFooter: true, isFixed: isFixed };

    return { isFixed: isFixed };
  };

  const onAddColumn = () => {
    const rowNodes = getListOfNodes(query).filter(
      filterNodesByParentId(tableId)
    );

    const batched = actions.history.merge();
    rowNodes.forEach((rowNode, i) => {
      const cellEl = (
        <Element
          is={TableCell}
          canvas
          borderAllShow={showBorder}
          elementProps={getElementPropsByRowType(rowNode)}
        />
      );
      const tree = query.parseReactElement(cellEl).toNodeTree();
      batched.addNodeTree(tree, rowNode.id);
    });
  };

  const onRemoveLastColumn = () => {
    const rowNodes = getListOfNodes(query).filter(
      filterNodesByParentId(tableId)
    );
    const batched = actions.history.merge();
    rowNodes.forEach((rowNode) => {
      const cellNodes = rowNode.data.nodes.map((nodeId) =>
        query.node(nodeId).get()
      );

      if (cellNodes.length > 1) {
        const lastCell = cellNodes[cellNodes.length - 1];
        batched.delete(lastCell.id);
      }
    });
  };

  const onAddRow = () => {
    const rowNodes = getListOfNodes(query).filter(
      filterNodesByParentId(tableId)
    );

    const firstRow = rowNodes.find(
      (row) => row.data.displayName === EDITOR_RESOLVERS.COMMON.TABLE_ROW
    );
    const numberOfColumns = firstRow ? firstRow.data.nodes.length : 0;

    const newRowTree = query
      .parseReactElement(
        <Element is={TableRow} canvas elementProps={{ isFixed: true }} />
      )
      .toNodeTree();

    const parentNode = query.node(tableId).get();

    const footerIndex = parentNode.data.nodes.findIndex((nodeId) => {
      const node = query.node(nodeId).get();
      return node.data.displayName === EDITOR_RESOLVERS.COMMON.TABLE_FOOTER;
    });

    const batched = actions.history.merge();

    const insertIndex =
      footerIndex >= 0 ? footerIndex : parentNode.data.nodes.length;

    batched.addNodeTree(newRowTree, tableId, insertIndex);

    const newRowId = newRowTree.rootNodeId;

    for (let colIndex = 0; colIndex < numberOfColumns; colIndex++) {
      const cellTree = query
        .parseReactElement(
          <Element
            is={TableCell}
            canvas
            elementProps={{
              ...getElementPropsByRowType(newRowTree.nodes[newRowId]),
              isFixed: true,
            }}
          />
        )
        .toNodeTree();

      cellTree.nodes[cellTree.rootNodeId].data.props = {
        ...TableCellDefaultProps,
        borderAllShow: showBorder,
        elementProps: getElementPropsByRowType(newRowTree.nodes[newRowId]),
      };

      batched.addNodeTree(cellTree, newRowId, colIndex);
    }
  };

  const onRemoveLastRow = () => {
    const rowNodes = getListOfNodes(query).filter(
      filterNodesByParentId(tableId)
    );

    if (rowNodes.length === 3) return;

    const lastRow = rowNodes[rowNodes.length - 1];
    actions.delete(lastRow.id);
  };

  const onSelectSourceClick = (e) => {
    const allNodes = getListOfNodes(query);
    const tableEl = findTableElementByParentId(allNodes, parent);
    const parentListSrc = getNodeSrc(tableEl);
    const pathToThisElement = findPathRecursive(allNodes, parent, '');

    setShowModal({ show: true, parentListSrc, pathToThisElement });
  };

  return (
    <SettingsWrapper>
      <TableModal
        currentValue={listSrc}
        documentJson={source?.structure ?? {}}
        showModal={showModal}
        setShowModal={setShowModal}
        changeSrcValue={changeSrcValue}
      />
      <SettingsRow sx={{ mb: 1 }}>
        <Typography>{`${t('property_name')}:`}</Typography>
        <Typography>{listSrc}</Typography>
      </SettingsRow>
      <SettingsRow sx={{ mb: 3 }}>
        <MyButton onClick={onSelectSourceClick} disabled={isFixed}>
          {t('select_source')}
        </MyButton>
      </SettingsRow>
      <SettingsRow sx={{ mb: 3 }}>
        <TableSegments
          options={TABLE_SEGMENT_OPTION_LIST}
          isFixed={isFixed}
          onAddColumn={onAddColumn}
          onRemoveColumn={onRemoveLastColumn}
          onAddRow={onAddRow}
          onRemoveRow={onRemoveLastRow}
        />
      </SettingsRow>
    </SettingsWrapper>
  );
};

export const TableDefaultProps = {
  background: 'no color',
  height: '30',
  listSrc: '',
  fontStyle: 'Helvetica, Arial, sans-serif',
  color: '#000000',
  showHeader: true,
  showFooter: true,
  showBorder: TableCellDefaultProps.borderAllShow,
  isValid: true,
  isDeletable: true,
};

Table.defaultProps = TableDefaultProps;

Table.propTypes = {
  background: PropTypes.string,
  height: PropTypes.string,
  listSrc: PropTypes.string,
  color: PropTypes.string,
  showHeader: PropTypes.bool,
  showFooter: PropTypes.bool,
  isValid: PropTypes.bool,
};

Table.craft = {
  displayName: EDITOR_RESOLVERS.COMMON.TABLE,
  name: EDITOR_RESOLVERS.COMMON.TABLE,
  props: TableDefaultProps,
  related: {
    settings: TableConfigPanel,
  },
  rules: {
    canMoveIn: (incoming, self, helper) => {
      return !(
        self.data.props.elementProps.isFixed &&
        incoming[0]?.data?.name === 'Placeholder'
      );
    },
  },
};

export default Table;
